import * as React from 'react';
import { QueryOptions, ReferenceField } from 'gossamer-unified';
import { FormControl } from './FormControl';
import { getRecordPickerQueryOptions, RecordPickerModal } from './RecordPicker';
import { UneditableReference } from './UneditableReference';
import styles from './ReferenceMultiSelect.module.css';

interface ReferenceMultiSelectProps {
  className?: string;
  editable: boolean;
  field: ReferenceField;
  modalTitle?: string;
  onChange?: (newValue: string[]) => void;
  queryOptions?: QueryOptions;
  suppressLink?: boolean;
  validity?: boolean;
  value: string[];
}

// a list of references to records of field.refEntity, shown as pills - when editable, each pill can be removed and
// new values added via the record picker modal
export const ReferenceMultiSelect: React.FC<ReferenceMultiSelectProps> = (props) => {
  const [showModal, setShowModal] = React.useState(false);
  const value = props.value || [];
  const onChange = props.onChange || (() => undefined);

  const onRowClick = (key: string) => {
    setShowModal(false);
    if (value.indexOf(key) === -1) {
      onChange([...value, key]);
    }
  };

  const pills = value.map((key) => (
    <div key={key} className={styles.Pill}>
      <UneditableReference
        refEntity={props.field.refEntity}
        suppressLink={props.editable || props.suppressLink}
        value={key}
        linkURL={props.field.linkURL}
      />
      {props.editable && (
        <button
          type="button"
          className={`ControlIcon valueClearer ${styles.PillRemove}`}
          aria-label="Remove"
          onClick={() => onChange(value.filter((v) => v !== key))}
        >
          ✖
        </button>
      )}
    </div>
  ));

  if (!props.editable) {
    return value.length ? <div className={`${styles.Pills} ${props.className || ''}`}>{pills}</div> : null;
  }

  return (
    <FormControl className={props.className} flexDirection="row" validity={props.validity}>
      <button type="button" className="ControlIcon valueSelector" onClick={() => setShowModal(true)}>
        🔍
      </button>
      <div className={styles.Pills}>{pills}</div>
      {showModal && (
        <RecordPickerModal
          field={props.field}
          modalTitle={props.modalTitle}
          onHide={() => setShowModal(false)}
          onRowClick={onRowClick}
          queryOptions={props.queryOptions || getRecordPickerQueryOptions(props.field)}
        />
      )}
    </FormControl>
  );
};
